import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ConversionCampaign, ConversionCampaignCommissionsResponse, ConversionCampaignFilters, ConversionCampaignGroupedEarning, ConversionCampaignLinksResponse, ConversionCampaignListResponse, ConversionCampaignSlice } from "../../types/conversions";
import { apiConfigs } from "../../constants/apiConfigs";
import { generateUniqueKey, getErrorMessage, isFresh } from "../../utils/helpers";
import { ERROR } from "../../constants/errors";
import { fetchConversionCampaignCommissions, fetchConversionCampaign, fetchConversionCampaignLinks, fetchConversionCampaigns, fetchConversionCampaignGroupedEarnings } from "../../services/api/conversionCampaigns";

const initialState: ConversionCampaignSlice = {
    filters: {
        page: 1,
        q: ''
    },
    filtered_list: {},
    applied_filter_id: null,
    list: [],
    count: 0,
    loading: true,
    last_fetched_at: null,
    page: 1,
    has_more: true,
    expired: true,
    items: {},
    itemLoading: true,
    linksLoading: true,
    links: {},
    commissions: {
        filtered_lists: {},
        applied_filter_id: null,
        applied_filters: {}
    },
    commissionsLoading: true,
    earnings: {
        data: [],
        loading: true,
        last_fetched_at: 0
    }
}

export const fetchConversionCampaignGroupedEarningsThunk = createAsyncThunk<{ data: ConversionCampaignGroupedEarning[] }, { force?: boolean, [key: string]: any }, { rejectValue: string }>(
    'conversionCampaigns/fetchConversionCampaignGroupedEarnings',
    async ({ force }, { getState, rejectWithValue }) => {
        const state = getState() as { conversionCampaigns: ConversionCampaignSlice };
        const STALE_TIME = apiConfigs.CONVERSION_CAMPAIGN_GROUPED_EARNINGS.staleTime;
        if (!force && isFresh(state.conversionCampaigns.earnings.last_fetched_at, STALE_TIME)) {
            return rejectWithValue(ERROR.CACHED);
        }
        try {
            const data = await fetchConversionCampaignGroupedEarnings();
            return { data: data as ConversionCampaignGroupedEarning[] };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch conversion campaign grouped earnings'));
        }
    }
)


export const fetchConversionCampaignsThunk = createAsyncThunk<{ data: ConversionCampaignListResponse, filters: ConversionCampaignFilters, filterId: string }, { filters: ConversionCampaignFilters, force?: boolean, [key: string]: any }, { rejectValue: string }>(
    'conversion-campaigns/fetchConversionCampaigns',
    async ({ filters, force }, { getState, rejectWithValue }) => {
        const {page} = filters;
        const filterId = generateUniqueKey(filters, ['page']);
        const state = getState() as { conversionCampaigns: ConversionCampaignSlice };
        if (page === 1 && !force && isFresh(state.conversionCampaigns.filtered_list[filterId]?.last_fetched_at, apiConfigs.FETCH_CONVERSION_CAMPAIGNS.staleTime)) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const response = await fetchConversionCampaigns(filters);
            return { data: response as ConversionCampaignListResponse, filters, filterId };
        } catch (error) {
            console.log(error);
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchConversionCampaignThunk = createAsyncThunk<{ campaign: ConversionCampaign, cached?: boolean }, { campaignId: string, force?: boolean }, { rejectValue: string }>(
    'conversionCampaigns/fetchConversionCampaign',
    async ({ campaignId, force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { conversionCampaigns: ConversionCampaignSlice };
            if (!force && isFresh(state.conversionCampaigns.items[campaignId]?.last_fetched_at, apiConfigs.FETCH_CONVERSION_CAMPAIGN.staleTime)) {
                return { campaign: state.conversionCampaigns.items[campaignId].campaign, cached: true };
            }
            const response = await fetchConversionCampaign(campaignId);
            return { campaign: response as ConversionCampaign };
        } catch (error) {
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)


export const fetchConversionCampaignLinksThunk = createAsyncThunk<{ data: ConversionCampaignLinksResponse, page: number, campaignId: string }, { campaignId: string, page: number, force?: boolean, [key: string]: any }, { rejectValue: string }>(
    'conversion-campaigns/fetchCampaignLinks',
    async ({ page, campaignId, force }, { getState, rejectWithValue }) => {

        const state = getState() as { conversionCampaigns: ConversionCampaignSlice };
        if (page === 1 && !force && isFresh(state.conversionCampaigns.links[campaignId]?.last_fetched_at, apiConfigs.FETCH_CONVERSION_CAMPAIGN_LINKS.staleTime)) {
            return rejectWithValue(ERROR.CACHED);
        }

        try {
            const response = await fetchConversionCampaignLinks(campaignId, { page });
            return { data: response as ConversionCampaignLinksResponse, page, campaignId };
        } catch (error) {
            console.log(error);
            return rejectWithValue(ERROR.NOT_FOUND);
        }

    }
)

export const fetchConversionCampaignCommissionsThunk = createAsyncThunk<{ data: ConversionCampaignCommissionsResponse, page: number, filterId: string, filters: any }, { page: number, force?: boolean, [key: string]: any, filters?: any }, { rejectValue: string }>(
    'conversionCampaigns/fetchConversionCampaignCommissions',
    async ({ page, force, filters }, { getState, rejectWithValue }) => {
        const state = getState() as { conversionCampaigns: ConversionCampaignSlice };
        const filterId = generateUniqueKey(filters, ['page']);
        if (page === 1 && !force && isFresh(state.conversionCampaigns.commissions.filtered_lists[filterId]?.last_fetched_at, apiConfigs.FETCH_CONVERSION_CAMPAIGN_COMMISSION.staleTime)) {
            return rejectWithValue(ERROR.CACHED);
        }


        try {
            const response = await fetchConversionCampaignCommissions({ page, ...filters });
            return { data: response as ConversionCampaignCommissionsResponse, page, filterId, filters };
        } catch (error) {
            console.log(error);
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)

const conversionCampaignSlice = createSlice({
    name: 'conversionCampaigns',
    initialState,
    reducers: {
        setConversionCampaignListFilters: (state, action) => {
            state.filters = action.payload;
        },
        setSearchQuery: (state, action) => {
            state.filters.q = action.payload;
            state.filters.page = 1;
        },
        setConversionCampaignCommissionsFilters: (state, action) => {
            state.commissions.applied_filters = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConversionCampaignsThunk.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(fetchConversionCampaignsThunk.fulfilled, (state, action) => {
                const { data, filterId, filters } = action.payload;
                const {page} = filters;
                const { items, count } = data;

                const prevList = state.filtered_list[filterId]?.list ?? [];
                const list = page === 1 ? items : [...prevList, ...items];
                
                state.filtered_list = {
                    ...state.filtered_list,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count
                    }
                }
                state.applied_filter_id = filterId;
                state.last_fetched_at = Date.now();
                state.loading = false;
            })
            .addCase(fetchConversionCampaignsThunk.rejected, (state) => {
                state.loading = false;
            })

            .addCase(fetchConversionCampaignThunk.pending, (state) => {
                state.itemLoading = true;
            })
            .addCase(fetchConversionCampaignThunk.fulfilled, (state, action) => {
                const { campaign, cached } = action.payload;
                state.items[campaign.uuid] = {
                    ...(state.items[campaign.uuid] || {}),
                    campaign,
                    ...(cached ? {} : { last_fetched_at: Date.now() })
                };
                state.itemLoading = false;
            })
            .addCase(fetchConversionCampaignThunk.rejected, (state) => {
                state.itemLoading = false;
            })

            .addCase(fetchConversionCampaignLinksThunk.pending, (state) => {
                state.linksLoading = true;
            })
            .addCase(fetchConversionCampaignLinksThunk.fulfilled, (state, action) => {
                const { data, page, campaignId } = action.payload;
                const { items, count } = data;

                const prevList = state.links[campaignId]?.list ?? [];
                const list = page === 1 ? items : [...prevList, ...items];

                state.links[campaignId] = {
                    ...(state.links[campaignId] ?? {}),
                    list,
                    page,
                    has_more: count > list.length,
                    count,
                    last_fetched_at: Date.now(),
                }

                state.linksLoading = false;
            })
            .addCase(fetchConversionCampaignLinksThunk.rejected, (state) => {
                state.linksLoading = false;
            })
            
            .addCase(fetchConversionCampaignCommissionsThunk.pending, (state) => {
                state.commissionsLoading = true;
            })
            .addCase(fetchConversionCampaignCommissionsThunk.fulfilled, (state, action) => {
                const { data, page, filterId, filters } = action.payload;
                const { items, count } = data;

                const prevList = state.commissions.filtered_lists[filterId]?.list ?? [];
                const list = page === 1 ? items : [...prevList, ...items];
                
                state.commissions.filtered_lists[filterId] = {
                    ...(state.commissions.filtered_lists[filterId] ?? {}),
                    list,
                    page,
                    has_more: count > list.length,
                    count,
                    last_fetched_at: Date.now(),
                }
                state.commissions.applied_filter_id = filterId;
                state.commissions.applied_filters = filters;
                state.commissionsLoading = false;

            })
            .addCase(fetchConversionCampaignCommissionsThunk.rejected, (state) => {
                state.commissionsLoading = false;
            })

            .addCase(fetchConversionCampaignGroupedEarningsThunk.pending, (state) => {
                state.earnings.loading = true;
            })
            .addCase(fetchConversionCampaignGroupedEarningsThunk.fulfilled, (state, action) => {
                const { data } = action.payload;
                state.earnings.data = data;
                state.earnings.last_fetched_at = Date.now();
                state.earnings.loading = false;
            })
            .addCase(fetchConversionCampaignGroupedEarningsThunk.rejected, (state) => {
                state.earnings.loading = false;
            })
    }
});

export const { setConversionCampaignListFilters, setSearchQuery, setConversionCampaignCommissionsFilters } = conversionCampaignSlice.actions;

export default conversionCampaignSlice.reducer;